import { useMemo } from "react";

import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import * as WidgetComps from './comps/WidgetComps/_EXPORTS'
import { optionDef } from "./comps/WidgetComps/WidgetBarChart";
import { toArray } from "oolib";

export const tabBarFormRendererOptions = [
  { value: "widget", display: "Widget" },  
  // { value: "setting", display: "Settings" },
  { value: "filters", display: "Filters" },
];

export const componentsMap = {
  ...Object.keys(WidgetComps)
    .reduce((acc, key) => {
      const {Component} = WidgetComps[key];
      return ({
        ...acc,
        ...({[key]: Component} || {})
      })
    },{})
}

export const allWidgetsBaseConfs = [
  ...Object.values(WidgetComps).flatMap(({optionDef}) => toArray(optionDef)), //this way optionDef could be an array as well as a object. (array helps when a single comp like WidgetGenerator handles multiple chart types)
];

/**
 * The widget options an Add Widget surface may offer THIS tenant.
 *
 * `allWidgetsBaseConfs` is every widget that EXISTS; this is every widget you
 * may ADD. The two must stay separate: WidgetRenderer resolves a SAVED widget's
 * widgetLabel, title fallback and under-construction display out of
 * `allWidgetsBaseConfs`, so a tenant-gated widget dropped from that list
 * renders unlabelled and untitled on a report that already uses it. Gate at the
 * add surface, never by emptying the registry.
 *
 * A widget opts in by putting `isAvailableToAdd({ platformConfigs })` on its
 * optionDef (see WidgetComps/WidgetTemp_SHI). No predicate means available to
 * everyone — which is every other widget today.
 *
 * A hook rather than a constant because availability depends on
 * platformConfigs, which is fetched and so unknowable when this module is
 * imported. Reading it non-reactively is safe: App renders nothing until the
 * platformConfigs query has succeeded.
 */
export const useAddableWidgetsBaseConfs = () => {
  const platformConfigs = useGetQueryData("platformConfigs");

  return useMemo(
    () =>
      allWidgetsBaseConfs.filter(
        ({ isAvailableToAdd }) =>
          !isAvailableToAdd || isAvailableToAdd({ platformConfigs })
      ),
    [platformConfigs]
  );
};


export const defaultWidgetConfigs = {
  ...Object.keys(WidgetComps)
    .reduce((acc, key) => {
      const {defaultConfig} = WidgetComps[key];
      return ({
        ...acc,
        ...({[key]: defaultConfig} || {})
      })
    },{})
};

export const processRawConfig = {
  ...Object.keys(WidgetComps)
    .reduce((acc, key) => {
      const {processRawConfigFn} = WidgetComps[key];
      return ({
        ...acc,
        ...({[key]: processRawConfigFn} || {})
      })
    },{})
}

export const configForms = {
  ...Object.keys(WidgetComps)
    .reduce((acc, key) => {
      const {ConfigForm} = WidgetComps[key];
      return ({
        ...acc,
        ...(ConfigForm ? {[key]: ConfigForm} : {})
      })
    },{})
}

export const someReqPropsUnsetFns = {
  ...Object.keys(WidgetComps)
    .reduce((acc, key) => {
      const {someReqPropsUnset} = WidgetComps[key];
      return ({
        ...acc,
        ...(someReqPropsUnset ? {[key]: someReqPropsUnset} : {})
      })
    },{})
}




export const sampleConfig = [
  {
    id: 1,
    gridColumn: "1/ 13",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["main.issueCategory"],

      style: { height: "100%" },
    },
  },
  {
    id: 2,
    gridColumn: "1/ 4",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["main.partner"],

      style: { height: "100%" },
    },
  },
  {
    id: 3,
    gridColumn: "4/ 7",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["tags.reason"],
    },
  },
  {
    id: 4,
    gridColumn: "7/ 10",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["tags.reason"],
    },
  },
  {
    id: 5,
    gridColumn: "10/ 13",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["tags.reason"],
    },
  },
  {
    id: 6,
    gridColumn: "1/ 13",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["main.issueCategory"],
    },
  },
  {
    id: 7,
    gridColumn: "1/ 5",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["main.partner"],
    },
  },
  {
    id: 8,
    gridColumn: "5/ 9",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["tags.reason"],
    },
  },
  {
    id: 9,
    gridColumn: "9/ 13",
    comp: "WidgetBarChart",
    props: {
      contentType: "safetyConcernData",

      dimensions: ["tags.reason"],
    },
  },
]
